"use client";

import { useMemo, type ReactNode } from "react";

import { ProfileMissionHeatmapMap, type ProfileMissionHeatmapMapLabels } from "./ProfileMissionHeatmapMap";
import {
  buildMissionHeatmapRegionSubtitle,
  buildMissionHeatmapSummaryStats,
  hasMissionHeatmapRenderableData,
  resolveMissionHeatmapMapCenter,
  type MissionHeatmapMapCenterInput,
  type MissionHeatmapProjectionInput,
} from "./missionHeatmapDisplay";
import { ensureRankedHeatmapLocations } from "./missionHeatmapExperienceRanking";

export type ProfileMissionHeatmapCardLabels = Readonly<{
  title: string;
  surroundingAreaSuffix?: string;
  emptyLabel: string;
  totalMissionsLabel: string;
  primaryOperatingMarketLabel: string;
  marketsServedLabel: string;
  lastMissionLabel: string;
  mapLabels: ProfileMissionHeatmapMapLabels;
  unavailableValue?: string;
}>;

export type ProfileMissionHeatmapCardProps = Readonly<{
  className?: string;
  componentInstance?: string;
  labels: ProfileMissionHeatmapCardLabels;
  locationDisplay?: string | null;
  profileLocationCenter?: MissionHeatmapMapCenterInput | null;
  projection: MissionHeatmapProjectionInput;
  title?: ReactNode;
}>;

function MissionHeatmapPinIcon({ className }: Readonly<{ className?: string }>) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M12 21s6-5.2 6-10a6 6 0 1 0-12 0c0 4.8 6 10 6 10Z"
        stroke="currentColor"
        strokeWidth="1.8"
      />
      <circle cx="12" cy="11" r="2.2" stroke="currentColor" strokeWidth="1.8" />
    </svg>
  );
}

function MissionHeatmapTrophyIcon({ className }: Readonly<{ className?: string }>) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M8 4h8v3a4 4 0 0 1-8 0V4Z"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinejoin="round"
      />
      <path d="M6 4H4v1a2 2 0 0 0 2 2M18 4h2v1a2 2 0 0 1-2 2" stroke="currentColor" strokeWidth="1.8" />
      <path d="M12 11v3M9 20h6M10 14h4v3H10v-3Z" stroke="currentColor" strokeWidth="1.8" />
    </svg>
  );
}

function MissionHeatmapGlobeIcon({ className }: Readonly<{ className?: string }>) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="8" stroke="currentColor" strokeWidth="1.8" />
      <path d="M4 12h16M12 4a12 12 0 0 1 0 16M12 4a12 12 0 0 0 0 16" stroke="currentColor" strokeWidth="1.8" />
    </svg>
  );
}

function MissionHeatmapTargetIcon({ className }: Readonly<{ className?: string }>) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="8" stroke="currentColor" strokeWidth="1.8" />
      <circle cx="12" cy="12" r="4" stroke="currentColor" strokeWidth="1.8" />
      <circle cx="12" cy="12" r="1.2" fill="currentColor" />
    </svg>
  );
}

function MissionHeatmapStat({
  className,
  icon,
  label,
  value,
  valueLines = 1,
}: Readonly<{
  className?: string;
  icon: ReactNode;
  label: string;
  value: string;
  valueLines?: 1 | 2;
}>) {
  return (
    <div className={["profile-mission-heatmap-card__stat", className].filter(Boolean).join(" ")}>
      <span className="profile-mission-heatmap-card__stat-label">
        <span className="profile-mission-heatmap-card__stat-icon" aria-hidden="true">
          {icon}
        </span>
        {label}
      </span>
      <strong
        className={[
          "profile-mission-heatmap-card__stat-value",
          valueLines === 2 ? "profile-mission-heatmap-card__stat-value--multiline" : null,
        ].filter(Boolean).join(" ")}
      >
        {value}
      </strong>
    </div>
  );
}

export function ProfileMissionHeatmapCard({
  className,
  componentInstance = "profileMissionHeatmapCard",
  labels,
  locationDisplay,
  profileLocationCenter,
  projection,
  title,
}: ProfileMissionHeatmapCardProps) {
  const unavailableValue = labels.unavailableValue ?? "—";
  const regionSubtitle = buildMissionHeatmapRegionSubtitle(
    locationDisplay,
    labels.surroundingAreaSuffix,
    projection.regionSubtitle,
  );
  const stats = buildMissionHeatmapSummaryStats(projection);
  const heatmapLocations = useMemo(
    () => ensureRankedHeatmapLocations(projection.heatmapLocations ?? []),
    [projection.heatmapLocations],
  );
  const mapCenter = resolveMissionHeatmapMapCenter(projection, profileLocationCenter);
  const renderable = hasMissionHeatmapRenderableData(projection);
  const showHeatmapMap = mapCenter !== null;

  return (
    <section
      className={["profile-mission-heatmap-card", className].filter(Boolean).join(" ")}
      data-component-instance={componentInstance}
      data-heatmap-location-count={String(heatmapLocations.length)}
      data-heatmap-map-state={showHeatmapMap ? (heatmapLocations.length > 0 ? "experience-map" : "profile-center") : "empty"}
      data-heatmap-renderable={renderable ? "true" : "false"}
    >
      <div className="profile-section-header">
        <div className="profile-section-header__title-group">
          <h2>{title ?? labels.title}</h2>
        </div>
      </div>

      {regionSubtitle ? (
        <p className="profile-mission-heatmap-card__subtitle">{regionSubtitle}</p>
      ) : null}

      {showHeatmapMap ? (
        <div className="profile-mission-heatmap-card__map">
          <ProfileMissionHeatmapMap
            center={mapCenter}
            componentInstance={`${componentInstance}Map`}
            labels={labels.mapLabels}
            locations={heatmapLocations}
            totalMissions={stats.totalMissions}
          />
        </div>
      ) : (
        <div className="profile-mission-heatmap-card__empty">{labels.emptyLabel}</div>
      )}

      <div className="profile-mission-heatmap-card__stats">
        <MissionHeatmapStat
          className="profile-mission-heatmap-card__stat--compact"
          icon={<MissionHeatmapPinIcon className="profile-mission-heatmap-card__stat-icon" />}
          label={labels.totalMissionsLabel}
          value={stats.totalMissions > 0 ? String(stats.totalMissions) : unavailableValue}
        />
        <MissionHeatmapStat
          className="profile-mission-heatmap-card__stat--wide"
          icon={<MissionHeatmapTrophyIcon className="profile-mission-heatmap-card__stat-icon" />}
          label={labels.primaryOperatingMarketLabel}
          value={stats.primaryOperatingMarket ?? unavailableValue}
          valueLines={2}
        />
        <MissionHeatmapStat
          className="profile-mission-heatmap-card__stat--compact"
          icon={<MissionHeatmapGlobeIcon className="profile-mission-heatmap-card__stat-icon" />}
          label={labels.marketsServedLabel}
          value={stats.marketsServed !== null ? String(stats.marketsServed) : unavailableValue}
        />
        <MissionHeatmapStat
          className="profile-mission-heatmap-card__stat--wide"
          icon={<MissionHeatmapTargetIcon className="profile-mission-heatmap-card__stat-icon" />}
          label={labels.lastMissionLabel}
          value={stats.lastMissionLabel ?? unavailableValue}
          valueLines={2}
        />
      </div>
    </section>
  );
}

export type {
  MissionHeatmapBucketInput,
  MissionHeatmapHistoryInput,
  MissionHeatmapIndustryInput,
  MissionHeatmapLocationInput,
  MissionHeatmapMapCenterInput,
  MissionHeatmapMetricsInput,
  MissionHeatmapProjectionInput,
} from "./missionHeatmapDisplay";
